import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Button, DatePicker, Spin } from 'antd';
import { useGpsOverview } from './useGpsOverview';
import { StyleGuide } from 'styles/StyleGuide';

export default function DatePickerOverview({
  selectedDate,
  onDateChange,
}: {
  selectedDate: Date;
  onDateChange: (date: Dayjs | null, dateString: string) => void;
}) {
  const selectedTime = dayjs(selectedDate).valueOf();
  // rc-picker 3 resets its viewed month when the controlled value's identity changes.
  const value = useMemo(() => dayjs(selectedTime), [selectedTime]);
  const selectedMonth = value.format('YYYY-MM');
  const [visibleMonth, setVisibleMonth] = useState(selectedMonth);
  const [open, setOpen] = useState(false);
  const { data, isFetching, isPending, error, refetch } = useGpsOverview(visibleMonth, open);

  useEffect(() => {
    setVisibleMonth(selectedMonth);
  }, [selectedMonth]);

  return (
    <DatePicker
      value={value}
      onChange={onDateChange}
      allowClear={false}
      onOpenChange={setOpen}
      onPanelChange={(date, mode) => {
        if (mode === 'date') setVisibleMonth(date.format('YYYY-MM'));
      }}
      cellRender={(current, info) => {
        if (info.type !== 'date') return info.originNode;
        // casting to Dayjs if number
        const cDate = typeof current === 'number' ? dayjs(current) : current;

        const hasMeasurements =
          !error &&
          cDate.format('YYYY-MM') === visibleMonth &&
          data?.includes(cDate.format('YYYY-MM-DD'));
        const style = hasMeasurements
          ? {
              border: `1px solid ${StyleGuide.palette.processing}`,
              backgroundColor: StyleGuide.palette.processing,
              color: '#fff',
              borderRadius: '50%',
            }
          : {};

        return (
          <div className="ant-picker-cell-inner" style={style}>
            {cDate.date()}
          </div>
        );
      }}
      renderExtraFooter={() => (
        <div style={{ lineHeight: 1.5, fontSize: 12, padding: '8px 0', maxWidth: 280 }}>
          {isFetching || (isPending && !error) ? (
            <div role="status" aria-live="polite">
              <Spin size="small" /> Indlæser månedens aktivitet…
            </div>
          ) : error ? (
            <div role="alert">
              Kunne ikke hente månedens aktivitet.
              <Button
                type="link"
                size="small"
                onClick={() => {
                  void refetch();
                }}
              >
                Prøv igen
              </Button>
            </div>
          ) : (
            <div role="status" aria-live="polite">
              {data?.length
                ? 'Markerede dage har registreret kørsel.'
                : 'Ingen registreret kørsel i denne måned.'}
              <div>Skift måned for at se ældre aktivitet.</div>
            </div>
          )}
        </div>
      )}
    />
  );
}
